import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Button, Card, GovernmentIdentity } from '@ministry/ui/internal';
import Check from 'lucide-react/dist/esm/icons/check.js';
import X from 'lucide-react/dist/esm/icons/x.js';

import { completePasswordReset } from '../lib/auth.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { checkPasswordClientSide, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../lib/password-policy.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';
import { PasswordField } from '../components/PasswordField.js';

/**
 * SCREEN_SPECS.md I4's completion step, reached from the reset email's link as
 * `/reset-password?token=...`. Same policy checklist as I23's own password-change form
 * (`checkPasswordClientSide` — fast feedback only; the server is authoritative), and the
 * SAME "expired/used link" copy the spec names: "This reset link has expired. Request a
 * new one." with a direct link back to the request step — `404 NOT_FOUND` from
 * `POST /auth/password/reset` covers an invalid, expired, AND already-used token alike
 * (verified live), so this screen does not attempt to distinguish them.
 */
export function PasswordResetPage(): React.JSX.Element {
  const t = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  const check = checkPasswordClientSide(newPassword, undefined);
  const passwordsMatch = confirmPassword !== '' && confirmPassword === newPassword;
  const confirmMismatch = touched && confirmPassword !== '' && !passwordsMatch;
  const canSubmit = token !== '' && newPassword !== '' && check.acceptable && passwordsMatch;

  const mutation = useMutation({ mutationFn: completePasswordReset });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    mutation.mutate({ token, newPassword });
  };

  const serverError = mutation.error;
  const isExpiredOrInvalid = serverError instanceof ApiError && serverError.code === 'NOT_FOUND';
  const serverRejections =
    serverError instanceof ApiError && serverError.code === 'VALIDATION_FAILED'
      ? serverError.fieldErrors.filter((fe) => fe.field === 'newPassword').map((fe) => fe.code)
      : [];
  const isRetryable = serverError instanceof NetworkError || (serverError instanceof ApiError && serverError.retryable);

  const checklistRow = (ok: boolean, label: string) => (
    <li className={`flex items-center gap-2 text-sm ${ok ? 'text-status-good-tint-fg' : 'text-text-muted'}`}>
      {ok ? <Check aria-hidden="true" width={14} height={14} /> : <X aria-hidden="true" width={14} height={14} />}
      <span>{label}</span>
    </li>
  );

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <header className="flex items-center justify-between gap-4 px-4 py-3">
        <GovernmentIdentity />
        <LocaleToggle />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4">
        <Card className="w-full max-w-sm p-6">
          <h1 className="mb-4 text-xl font-semibold text-text-heading">{t('passwordReset.complete.title')}</h1>

          {token === '' ? (
            <AlertBanner variant="critical" title={t('passwordReset.complete.noToken.title')}>
              {t('passwordReset.complete.noToken.body')}
            </AlertBanner>
          ) : mutation.isSuccess ? (
            <AlertBanner variant="info" title={t('passwordReset.complete.success.title')}>
              {t('passwordReset.complete.success.body', { count: mutation.data.sessionsRevoked })}
            </AlertBanner>
          ) : (
            <>
              {isExpiredOrInvalid ? (
                <div className="mb-4">
                  <AlertBanner variant="critical" title={t('passwordReset.complete.expired.title')}>
                    {t('passwordReset.complete.expired.body')}
                  </AlertBanner>
                </div>
              ) : serverError ? (
                <div className="mb-4">
                  <AlertBanner variant="critical" title={isRetryable ? t('passwordReset.complete.error.network') : t('passwordReset.complete.error.generic')} />
                </div>
              ) : null}

              <form onSubmit={submit} noValidate className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <PasswordField
                    id="password-reset-new"
                    label={t('accountSettings.password.new')}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <ul className="flex flex-col gap-1 rounded-md bg-surface-base p-3">
                    {checklistRow(
                      newPassword.length >= PASSWORD_MIN_LENGTH && newPassword.length <= PASSWORD_MAX_LENGTH,
                      t('accountSettings.password.rule.length', { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }),
                    )}
                    {checklistRow(!check.rejections.includes('blocked_term'), t('accountSettings.password.rule.notCommon'))}
                  </ul>
                  {serverRejections.length > 0 ? (
                    <p className="text-sm text-status-critical-tint-fg">
                      {serverRejections.map((code) => t(`accountSettings.password.rule.serverRejected.${code}`)).join(' ')}
                    </p>
                  ) : null}
                </div>
                <PasswordField
                  id="password-reset-confirm"
                  label={t('accountSettings.password.confirm')}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setTouched(true)}
                  errorText={confirmMismatch ? t('accountSettings.password.rule.mismatch') : undefined}
                  required
                />
                <Button type="submit" disabled={!canSubmit} loading={mutation.isPending} loadingLabel={t('passwordReset.complete.submitting')}>
                  {t('passwordReset.complete.submit')}
                </Button>
              </form>
            </>
          )}

          <Link to="/reset-password/request" className="mt-4 inline-block text-sm font-semibold text-text-link underline">
            {t('passwordReset.complete.requestNewLink')}
          </Link>
        </Card>
      </div>
    </div>
  );
}
